import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useSidebarStore } from '@/stores/sidebarStore'
import { useCinemaStore } from '@/stores/cinemaStore'

/**
 * Global keyboard shortcuts for sidebar navigation.
 * Call once in AppLayout.
 *
 * Shortcuts:
 * - Ctrl/Cmd + [ → Toggle icon rail
 * - Ctrl/Cmd + ] → Cycle sidebar (expanded → collapsed → hidden on ramp; expanded ↔ collapsed elsewhere)
 * - Ctrl/Cmd + \ → Focus mode (ramp page only)
 * - Escape → Exit focus mode
 */
export function useSidebarShortcuts() {
  const { cycleSidebar, toggleRail, mainState, railVisible, enterFocusMode, exitFocusMode, setMainState } =
    useSidebarStore()
  const { pathname } = useLocation()
  const isRampPage = pathname.endsWith('/ramp')

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip when typing in inputs
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      const modifier = e.metaKey || e.ctrlKey

      if (modifier && e.key === '[') {
        e.preventDefault()
        toggleRail()
      } else if (modifier && e.key === ']') {
        e.preventDefault()
        if (isRampPage) {
          cycleSidebar()
        } else {
          // On non-ramp pages, only toggle between expanded and collapsed
          setMainState(mainState === 'expanded' ? 'collapsed' : 'expanded')
        }
      } else if (modifier && e.key === '\\') {
        e.preventDefault()
        // Focus mode only available on the ramp page
        if (isRampPage) {
          if (mainState === 'hidden' && !railVisible) {
            exitFocusMode()
          } else {
            enterFocusMode()
          }
        }
      } else if (e.key === 'Escape') {
        // Cinema mode owns Escape. It hides the sidebar via focus mode, so
        // without this bail a single press would restore the sidebar while all
        // the page chrome stayed hidden — a broken half-state.
        if (useCinemaStore.getState().active) return
        if (mainState === 'hidden') {
          exitFocusMode()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [cycleSidebar, toggleRail, enterFocusMode, exitFocusMode, mainState, railVisible, isRampPage, setMainState])
}
